import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { formatAccountLabel } from '@/features/accounts/accounts.api';
import { useAccounts } from '@/features/accounts/useAccounts';
import { useAccountColors } from '@/features/accounts/useAccountColors';
import { ConversationFilterTabs, type ConversationFilter } from '@/features/conversations/ConversationFilterTabs';
import { ConversationSearchInput } from '@/features/conversations/ConversationSearchInput';
import { ConversationRow, type AccountBadge } from '@/features/conversations/ConversationRow';
import { MergedConversationRow } from '@/features/conversations/MergedConversationRow';
import {
  collapseGroupRuns,
  groupKeyOf,
  type DisplayRow,
} from '@/features/conversations/group-runs';
import { matchesSearch } from '@/features/conversations/search-match';
import { useConversations } from '@/features/conversations/useConversations';
import type {
  Conversation,
  ConversationView,
} from '@/features/conversations/conversations.api';

interface ConversationListProps {
  /** `null` = aggregate "所有账号" mode — every account's conversations, each
   *  row's WA badge replaced by a colored account badge (see
   *  `accountBadgeById` below). A UUID scopes the list to a single account
   *  (WA badge, unchanged). */
  accountId: string | null;
}

/** Estimated row height for the virtualizer's initial layout. The
 *  `measureElement` hook below replaces this with actual measurements as
 *  rows mount; the estimate just bounds the initial total height so the
 *  scrollbar isn't wildly wrong before any row has measured. */
const ESTIMATED_ROW_HEIGHT = 84;

/** Trigger `fetchNextPage` when the rendered window's tail is this close to
 *  the end of the loaded data. */
const PREFETCH_THRESHOLD = 5;

/**
 * The conversation list body — either a single account's inbox, or the
 * aggregate "所有账号" queue across every account when `accountId` is `null`.
 *
 * Owns its own filter tab state and search state (these are pure UI; the
 * queryKey changes whenever they change, so a real refetch happens
 * automatically). Renders three vertically-stacked regions:
 *
 *   1. Filter tabs (always visible)
 *   2. Search input (always visible)
 *   3. Virtualized list body (empty / loading / error / data states)
 *
 * In aggregate mode each row's WA badge is replaced by a colored badge
 * naming the conversation's account (via `ConversationRow`'s `accountBadge`
 * prop), built from the cached accounts list + `useAccountColors`, so an
 * agent can tell which line a customer wrote to at a glance. A row whose
 * account isn't in the accounts cache yet falls back to the plain WA badge
 * — see `accountBadgeById`. Per-account mode never computes the map (stays
 * `null`), so its rendering — the WA badge, unchanged — is untouched.
 */
export function ConversationList({ accountId }: ConversationListProps): React.JSX.Element {
  const [filter, setFilter] = useState<ConversationFilter>('all');
  const [search, setSearch] = useState('');

  const { view } = filterToParams(filter);
  const query = useConversations({ accountId, view, search });

  // Account badge lookup for the aggregate-mode row badge. Reuses the
  // accounts list's OWN cached query (kept warm by `AccountSidebar` mounting
  // `useAccounts` at the same time) plus the shared color assignment — this
  // never issues a fetch of its own.
  const accountsQuery = useAccounts();
  const accountColors = useAccountColors();
  const accountBadgeById = useMemo(() => {
    if (accountId !== null) return null;
    const map = new Map<string, AccountBadge>();
    for (const account of accountsQuery.data ?? []) {
      const color = accountColors.get(account.id);
      // Should always be defined — `accountColors` is built from this same
      // accounts list — but skip defensively rather than badge with no color.
      if (!color) continue;
      map.set(account.id, { label: formatAccountLabel(account), badgeClassName: color.badge });
    }
    return map;
  }, [accountId, accountsQuery.data, accountColors]);

  // Flatten paginated pages into a single array for the virtualizer. Memo
  // on the source-of-truth pages so the row identity is stable across
  // re-renders (avoids tearing in react-virtual's measurement cache).
  // Deduped by conversation id, FIRST occurrence wins: offset pagination has
  // an inherent duplicate window — a row that moves between two page fetches
  // can appear on both pages — and grouped ordering (whole blocks moving at
  // once) widens it. A duplicated id would also mean duplicate React keys in
  // the virtualizer. Cursor pagination is the real fix (out of scope); this
  // is the cheap client-side mitigation.
  const allConversations: Conversation[] = useMemo(() => {
    if (!query.data) return [];
    const seen = new Set<string>();
    const flat: Conversation[] = [];
    for (const page of query.data.pages) {
      for (const item of page.items) {
        if (seen.has(item.id)) continue;
        seen.add(item.id);
        flat.push(item);
      }
    }
    return flat;
  }, [query.data]);

  // GUARD filter, not the real filter. The server applies `q=` (the hook
  // forwards `search`), so every FETCHED page is already filtered — but the
  // WS projector (`projectMessageNewIntoLists` in `@/lib/realtime/projection`)
  // writes new-message rows into every cached conversations list, and its
  // membership gate checks account scope and `view`, NOT `search`: a row that
  // does not match the query can be injected into this list's cache between
  // refetches. This memo re-asserts the search locally to hide those rows.
  // Because it also re-filters rows the server DID return, its semantics must
  // mirror the backend's exactly — `matchesSearch` (search-match.ts) carries
  // the digit-normalization and literal-wildcard rules; a stricter guard
  // would wrongly hide server hits (the old raw-substring guard hid a
  // "+6013..." row when the operator typed "0136 139").
  const filtered = useMemo(() => {
    if (!search.trim()) return allConversations;
    return allConversations.filter((c) => matchesSearch(c.contact, search));
  }, [allConversations, search]);

  // Display rows — aggregate mode collapses each contiguous same-phone run
  // (2+ conversations sharing `phone_e164 ?? peer_id`) into ONE merged row
  // that opens the merged thread; single-member runs stay ordinary
  // per-conversation rows. A run only fuses rows sharing the key AND
  // pinnedness, so a fully-pinned same-key run merges into one pinned block
  // too, while a partially-pinned customer splits into a pinned block/single
  // plus an unpinned block. Derived from the ALREADY-ORDERED
  // `filtered` list WITHOUT re-sorting (the server's `group=phone` ordering
  // is the authority; see `useConversations` / `collapseGroupRuns`). This
  // memo sits DOWNSTREAM of the id-dedup above, so a cross-page duplicate
  // can never appear twice inside one merged block. Per-account mode maps
  // every conversation to a single row — a single account can never have two
  // rows for one number — so those rows render exactly as before.
  const displayRows: DisplayRow[] = useMemo(
    () =>
      accountId === null
        ? collapseGroupRuns(filtered)
        : filtered.map((conversation) => ({ kind: 'single' as const, conversation })),
    [accountId, filtered],
  );

  // Thread membership per group key — EVERY loaded conversation id sharing
  // the key, REGARDLESS of pinnedness, in list order. Pin-split blocks must
  // not fragment the thread's member set: a partially-pinned customer
  // renders as TWO display blocks (pinned + unpinned) sharing one key, and
  // the thread opened from either block must carry every sibling's id — the
  // WS mirror (`thread.memberIds.includes(...)`), the viewing
  // unread-suppression, the viewed auto-read POST and ThreadPanel's
  // mark-read fan-out all key off `memberIds`, so a set built from one
  // block alone would let the OTHER block's unread badges climb while the
  // operator is viewing that very customer (the thread HISTORY is complete
  // either way — the server fetches by key — which makes the lying badge
  // worse). Aggregate mode only: per-account lists never render merged rows.
  const threadMemberIdsByKey = useMemo(() => {
    if (accountId !== null) return null;
    const map = new Map<string, string[]>();
    for (const conversation of filtered) {
      const key = groupKeyOf(conversation);
      const ids = map.get(key);
      if (ids) {
        ids.push(conversation.id);
      } else {
        map.set(key, [conversation.id]);
      }
    }
    return map;
  }, [accountId, filtered]);

  // Virtualizer setup — react-virtual handles its own scroll container
  // (the parent div with overflow-auto). We pass `measureElement` so each
  // row's actual rendered height feeds back into the layout once mounted.
  // The count is DISPLAY rows (merged blocks count once), not conversations.
  const parentRef = useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: displayRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 6,
    measureElement: (node) => node.getBoundingClientRect().height,
  });

  // Infinite-scroll trigger: when the rendered tail reaches within
  // PREFETCH_THRESHOLD of the loaded list end, kick off the next page.
  const virtualItems = rowVirtualizer.getVirtualItems();
  const lastVirtualIndex = virtualItems.length > 0
    ? virtualItems[virtualItems.length - 1].index
    : 0;

  useEffect(() => {
    if (
      query.hasNextPage &&
      !query.isFetchingNextPage &&
      lastVirtualIndex >= displayRows.length - PREFETCH_THRESHOLD
    ) {
      void query.fetchNextPage();
    }
  }, [
    lastVirtualIndex,
    displayRows.length,
    query.hasNextPage,
    query.isFetchingNextPage,
    query,
  ]);

  // --- Render branches ----------------------------------------------------

  if (query.isLoading) {
    return (
      <div className="flex h-full flex-col gap-2">
        <FilterStrip filter={filter} setFilter={setFilter} setSearch={setSearch} />
        <ul className="flex flex-col py-1" data-testid="conversation-skeletons">
          {[0, 1, 2, 3].map((i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-3">
              <Skeleton className="size-10 rounded-full" />
              <div className="flex flex-1 flex-col gap-1.5">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-2.5 w-48" />
              </div>
              <Skeleton className="h-2 w-10" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="flex h-full flex-col gap-2">
        <FilterStrip filter={filter} setFilter={setFilter} setSearch={setSearch} />
        <div className="flex flex-col items-start gap-2 px-4 py-4 text-sm text-muted-foreground">
          <p>Failed to load conversations</p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => query.refetch()}
          >
            <RefreshCw className="mr-2 size-3.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="flex h-full flex-col gap-2">
        <FilterStrip filter={filter} setFilter={setFilter} setSearch={setSearch} />
        <div className="px-4 py-6 text-sm text-muted-foreground">
          {search.trim() ? (
            <>No conversations match <span className="font-medium text-foreground">&quot;{search}&quot;</span>.</>
          ) : accountId === null ? (
            <>No conversations yet.</>
          ) : (
            <>No conversations yet for this account.</>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <FilterStrip filter={filter} setFilter={setFilter} setSearch={setSearch} />

      <div
        ref={parentRef}
        className="min-h-0 flex-1 overflow-y-auto"
        data-testid="conversation-virtual-scroll"
      >
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualItems.map((virtualRow) => {
            const row = displayRows[virtualRow.index];
            const virtualStyle: React.CSSProperties = {
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            };
            if (row.kind === 'merged') {
              // Key includes the block head's id, not just the group key —
              // a pinned row can split one phone's rows into TWO merged
              // blocks sharing the key, and React keys must stay unique.
              return (
                <MergedConversationRow
                  key={`merged:${row.key}:${row.members[0].id}`}
                  groupKey={row.key}
                  members={row.members}
                  threadMemberIds={threadMemberIdsByKey?.get(row.key)}
                  dataIndex={virtualRow.index}
                  measureRef={(node) => rowVirtualizer.measureElement(node)}
                  style={virtualStyle}
                />
              );
            }
            const conversation = row.conversation;
            return (
              <ConversationRow
                key={conversation.id}
                conversation={conversation}
                dataIndex={virtualRow.index}
                accountBadge={accountBadgeById?.get(conversation.account_id)}
                measureRef={(node) => rowVirtualizer.measureElement(node)}
                style={virtualStyle}
              />
            );
          })}
        </div>

        {query.isFetchingNextPage && (
          <div className="flex items-center gap-3 px-3 py-3" aria-live="polite">
            <Skeleton className="size-10 rounded-full" />
            <Skeleton className="h-3 flex-1" />
          </div>
        )}
        {!query.hasNextPage && filtered.length > 10 && (
          <p className="py-3 text-center text-xs text-muted-foreground">
            No more conversations
          </p>
        )}
      </div>
    </div>
  );
}

// --- Helpers ---------------------------------------------------------------

interface FilterStripProps {
  filter: ConversationFilter;
  setFilter: (f: ConversationFilter) => void;
  setSearch: (s: string) => void;
}

function FilterStrip({
  filter,
  setFilter,
  setSearch,
}: FilterStripProps): React.JSX.Element {
  return (
    <div className="shrink-0 space-y-3 px-4 py-2">
      <ConversationFilterTabs value={filter} onValueChange={setFilter} />
      <ConversationSearchInput onDebouncedChange={setSearch} />
    </div>
  );
}

/**
 * Translate a UI filter into the backend's `view=` query param. "All"
 * produces no params (server default four-zone ordering); "Unread & Pin" /
 * "Cuci" / "Special" forward the matching `ConversationView` literal. See
 * `ConversationFilterTabs` for the full mapping table.
 */
function filterToParams(
  filter: ConversationFilter,
): { view?: ConversationView } {
  switch (filter) {
    case 'unread_pin':
      return { view: 'unread_pin' };
    case 'cuci':
      return { view: 'cuci' };
    case 'special':
      return { view: 'special' };
    case 'all':
    default:
      return {};
  }
}
